import React, { useRef, useState, useEffect } from 'react';
import { chatService } from '../../services/chatService';

interface TagSuggestion {
  suggested_tag: string;
  confidence: number;
  confidence_level: 'high' | 'medium' | 'low';
  alternative_tags?: Array<{ tag: string; confidence: number }>;
  reasoning?: string;
}

interface TagValidation {
  is_reasonable: boolean;
  match_score: number;
  warning?: string;
  suggested_alternative?: string;
}

interface KnowledgeFileUploadProps {
  onSuccess?: () => void; // Callback when document is successfully added
}

const KnowledgeFileUpload: React.FC<KnowledgeFileUploadProps> = ({ onSuccess }) => {
  const knowledgeFileInputRef = useRef<HTMLInputElement>(null);
  const [selectedKnowledgeFile, setSelectedKnowledgeFile] = useState<File | null>(null);
  const [uploadingKnowledge, setUploadingKnowledge] = useState(false);
  const [knowledgeUploadStatus, setKnowledgeUploadStatus] = useState<string>('');
  const [uploadedFilename, setUploadedFilename] = useState<string>('');
  const [addingKnowledgeFile, setAddingKnowledgeFile] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedParsingStrategy, setSelectedParsingStrategy] = useState<string>('');
  const [tags, setTags] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [parsingStrategies, setParsingStrategies] = useState<string[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [addNotification, setAddNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [fileUuid, setFileUuid] = useState<string>('');
  const [isPolling, setIsPolling] = useState(false);
  const [suggestion, setSuggestion] = useState<TagSuggestion | null>(null);
  const [validation, setValidation] = useState<TagValidation | null>(null);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [requestingSuggestion, setRequestingSuggestion] = useState(false);
  const [aiFilledType, setAiFilledType] = useState(false);
  const [aiFilledStrategy, setAiFilledStrategy] = useState(false);

  const handleKnowledgeFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedKnowledgeFile(file);
      setKnowledgeUploadStatus('');
    }
  };

  const handleKnowledgeBrowseFiles = () => {
    knowledgeFileInputRef.current?.click();
  };

  const handleKnowledgeFileUpload = async () => {
    if (!selectedKnowledgeFile) return;

    setUploadingKnowledge(true);
    setKnowledgeUploadStatus('Uploading...');
    setSuggestion(null);
    setValidation(null);

    try {
      // Step 1: Upload file
      const response = await chatService.uploadKnowledgeFile(selectedKnowledgeFile);
      const data = response?.data ?? response;
      console.log('Knowledge file uploaded successfully!', data);
      const filename = data.filename || selectedKnowledgeFile.name;
      setUploadedFilename(filename);
      setSelectedKnowledgeFile(null);
      if (knowledgeFileInputRef.current) {
        knowledgeFileInputRef.current.value = '';
      }

      // Step 2: Request AI tag suggestion
      setRequestingSuggestion(true);
      setKnowledgeUploadStatus('Getting AI tag suggestion...');

      const suggestionResponse = await chatService.addKnowledgeFile(filename, {
        type: selectedType || 'DDQ',
        parsing_strategy: selectedParsingStrategy || 'Normal',
        auto_suggest_tag: true
      });

      const suggestionData = suggestionResponse?.data ?? suggestionResponse;

      if (suggestionData.status === 'awaiting_tag_confirmation' && suggestionData.suggestion) {
        setSuggestion(suggestionData.suggestion);
        setSelectedTag(suggestionData.suggestion.suggested_tag);
        setFileUuid(suggestionData.file_uuid);

        // Set type and strategy from API response if available
        if (suggestionData.type) {
          setSelectedType(suggestionData.type);
          setAiFilledType(true);
        }
        if (suggestionData.strategy) {
          setSelectedParsingStrategy(suggestionData.strategy);
          setAiFilledStrategy(true);
        }

        setAwaitingConfirmation(true);
        setKnowledgeUploadStatus('AI tag suggestion ready!');
      } else {
        // Fallback to standard flow
        setKnowledgeUploadStatus('Knowledge file uploaded successfully!');
      }
    } catch (error) {
      console.error('Failed to upload knowledge file:', error);
      setKnowledgeUploadStatus('Upload failed. Please try again.');
      setSuggestion(null);
      setAwaitingConfirmation(false);
    } finally {
      setUploadingKnowledge(false);
      setRequestingSuggestion(false);
    }
  };

  const fetchChatMeta = async () => {
    setLoadingMeta(true);
    try {
      const metaData = await chatService.getChatMeta();
      setTags(metaData.tags || []);
      setTypes(metaData.type_list || []);
      setParsingStrategies(metaData.parsing_strategy || []);
    } catch (error) {
      console.error('Failed to fetch chat meta:', error);
    } finally {
      setLoadingMeta(false);
    }
  };

  const pollFileStatus = async (uuid: string) => {
    setIsPolling(true);
    const pollInterval = setInterval(async () => {
      try {
        const statusData = await chatService.getKnowledgeFileStatus(uuid);

        if (statusData.status === 'success') {
          clearInterval(pollInterval);
          setIsPolling(false);
          setAddNotification({ type: 'success', message: 'Knowledge file added successfully!' });

          // Reset all fields
          setSelectedKnowledgeFile(null);
          setUploadedFilename('');
          setKnowledgeUploadStatus('');
          setSelectedTag('');
          setSelectedType('');
          setSelectedParsingStrategy('');
          setFileUuid('');
          setAwaitingConfirmation(false);
          setSuggestion(null);
          setValidation(null);
          setAiFilledType(false);
          setAiFilledStrategy(false);
          if (knowledgeFileInputRef.current) {
            knowledgeFileInputRef.current.value = '';
          }

          // Call onSuccess callback to close modal
          if (onSuccess) {
            onSuccess();
          }
        } else if (statusData.status === 'failed') {
          clearInterval(pollInterval);
          setIsPolling(false);
          setAddNotification({
            type: 'error',
            message: `Failed to process file: ${statusData.error_message || 'Unknown error occurred'}`
          });
          setFileUuid('');
        }
      } catch (error) {
        console.error('Failed to check file status:', error);
        clearInterval(pollInterval);
        setIsPolling(false);
        setAddNotification({
          type: 'error',
          message: 'Failed to check file processing status'
        });
        setFileUuid('');
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 5 minutes to prevent infinite polling
    setTimeout(() => {
      clearInterval(pollInterval);
      if (isPolling) {
        setIsPolling(false);
        setAddNotification({
          type: 'error',
          message: 'File processing timed out. Please check the status manually.'
        });
        setFileUuid('');
      }
    }, 300000); // 5 minutes
  };

  const handleTagChange = async (newTag: string) => {
    setSelectedTag(newTag);
    setValidation(null);

    // Validate if different from suggestion and we have a suggestion
    if (suggestion && newTag !== suggestion.suggested_tag && fileUuid) {
      try {
        const validationResult = await chatService.validateTag(fileUuid, newTag);
        setValidation(validationResult);
      } catch (error) {
        console.error('Failed to validate tag:', error);
      }
    }
  };

  const handleConfirmTag = async () => {
    if (!fileUuid || !selectedTag) return;

    setAddingKnowledgeFile(true);
    setAddNotification(null);

    try {
      const confirmResponse = await chatService.confirmTag(
        fileUuid,
        selectedTag,
        suggestion ? selectedTag !== suggestion.suggested_tag : false
      );

      // Show success notification
      setAddNotification({
        type: 'success',
        message: 'Document added successfully!'
      });

      // Reset all fields
      setSelectedKnowledgeFile(null);
      setUploadedFilename('');
      setSelectedTag('');
      setSelectedType('');
      setSelectedParsingStrategy('');
      setKnowledgeUploadStatus('');
      setAwaitingConfirmation(false);
      setSuggestion(null);
      setValidation(null);
      setFileUuid('');
      setAiFilledType(false);
      setAiFilledStrategy(false);
      if (knowledgeFileInputRef.current) {
        knowledgeFileInputRef.current.value = '';
      }

      // Call onSuccess callback to close modal
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to confirm tag:', error);
      setAddNotification({
        type: 'error',
        message: 'Failed to confirm tag. Please try again.'
      });
    } finally {
      setAddingKnowledgeFile(false);
    }
  };

  const handleAddKnowledgeFile = async () => {
    if (!uploadedFilename) return;

    setAddingKnowledgeFile(true);
    setAddNotification(null);
    try {
      const response = await chatService.addKnowledgeFile(
        uploadedFilename, {
        tag: selectedTag,
        type: selectedType,
        parsing_strategy: selectedParsingStrategy,
        auto_suggest_tag: false
      });

      const data = response?.data ?? response;
      if (data.file_uuid) {
        setFileUuid(data.file_uuid);
        setAddNotification({ type: 'success', message: data.message || 'Knowledge file processing started...' });
        // Start polling for status
        pollFileStatus(data.file_uuid);
      } else {
        setAddNotification({ type: 'error', message: 'Invalid response from server' });
      }
    } catch (error) {
      console.error('Failed to add knowledge file:', error);
      setAddNotification({ type: 'error', message: 'Failed to add knowledge file. Please try again.' });
    } finally {
      setAddingKnowledgeFile(false);
    }
  };

  useEffect(() => {
    fetchChatMeta();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (isPolling) {
        setIsPolling(false);
      }
    };
  }, [isPolling]);

  const handleKnowledgeDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleKnowledgeDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedKnowledgeFile(file);
      setKnowledgeUploadStatus('');
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Upload a Knowledge Document</h3>
      <p className="text-gray-600 mb-4">Choose a file</p>
      
      <input
        type="file"
        ref={knowledgeFileInputRef}
        onChange={handleKnowledgeFileSelect}
        className="hidden"
        accept="*/*"
      />
      
      <div
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-adalabs-blue-400 transition-colors cursor-pointer"
        onDragOver={handleKnowledgeDragOver}
        onDrop={handleKnowledgeDrop}
        onClick={handleKnowledgeBrowseFiles}
      >
        <p className="text-gray-600">
          {selectedKnowledgeFile ? `Selected: ${selectedKnowledgeFile.name}` : 'Drag and drop files here'}
        </p>
      </div>
      
      <div className="space-y-4 mt-4">
        <button 
          className="btn-secondary px-6 py-2 text-sm"
          onClick={handleKnowledgeBrowseFiles}
        >
          Browse files
        </button>
        
        {selectedKnowledgeFile && (
          <button 
            className="btn-accent px-6 py-2 text-sm ml-2"
            onClick={handleKnowledgeFileUpload}
            disabled={uploadingKnowledge}
          >
            {uploadingKnowledge ? 'Uploading...' : 'Upload File'}
          </button>
        )}
      </div>
      
      {knowledgeUploadStatus && (
        <div className={`mt-6 rounded-xl transition-all duration-300 ${
          knowledgeUploadStatus.includes('success') || knowledgeUploadStatus.includes('ready')
            ? 'bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 shadow-lg'
            : knowledgeUploadStatus.includes('Getting')
            ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-lg'
            : knowledgeUploadStatus.includes('Uploading')
            ? 'bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 shadow-lg'
            : 'bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 shadow-lg'
        }`}>

          {/* Uploading State */}
          {knowledgeUploadStatus.includes('Uploading') && (
            <div className="p-6">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <svg className="w-12 h-12 text-purple-500 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin"></div>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-semibold text-gray-800">Uploading Document</h4>
                    <span className="text-sm font-medium text-purple-600">Processing...</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full animate-pulse" style={{ width: '70%' }}></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Preparing your file for analysis...</p>
                </div>
              </div>
            </div>
          )}

          {/* AI Analysis Loading State */}
          {requestingSuggestion && (
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="relative">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-indigo-600 rounded-full opacity-30 blur animate-ping"></div>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-800 mb-1">AI Analysis in Progress</h4>
                  <p className="text-sm text-gray-600">Our AI is analyzing your document to suggest the best tag...</p>
                </div>
              </div>

              {/* Skeleton Loader */}
              <div className="space-y-3 animate-pulse">
                <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded bg-[length:200%_100%] animate-shimmer"></div>
                <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-3/4 bg-[length:200%_100%] animate-shimmer"></div>
                <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-5/6 bg-[length:200%_100%] animate-shimmer"></div>
              </div>
            </div>
          )}

          {suggestion && awaitingConfirmation && (
            <div className="p-6">
              {/* Main Confidence Card */}
              <div className="mb-6 p-6 bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">AI Suggested Tag</span>
                      <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                        suggestion.confidence_level === 'high'
                          ? 'bg-green-100 text-green-700'
                          : suggestion.confidence_level === 'medium'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {suggestion.confidence_level === 'high' ? 'High Confidence' : suggestion.confidence_level === 'medium' ? 'Moderate Confidence' : 'Low Confidence'}
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{suggestion.suggested_tag}</h3>
                    {suggestion.reasoning && (
                      <p className="text-sm text-gray-600 leading-relaxed">{suggestion.reasoning}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Alternative Tags as Pills */}
              {suggestion.alternative_tags && suggestion.alternative_tags.length > 0 && (
                <div className="mb-6">
                  <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Alternative Suggestions
                  </h5>
                  <div className="flex flex-wrap gap-2">
                    {suggestion.alternative_tags.slice(0, 3).map((alt, index) => (
                      <button
                        key={index}
                        onClick={() => handleTagChange(alt.tag)}
                        className="group relative px-4 py-2 bg-white border-2 border-gray-300 rounded-full hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 hover:shadow-md cursor-pointer"
                      >
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">{alt.tag}</span>
                          <span className="text-xs font-semibold text-gray-500 group-hover:text-blue-600">{Math.round(alt.confidence * 100)}%</span>
                        </div>
                        <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full transition-all duration-200 w-0 group-hover:w-full"></div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Auto-fill Info */}
              {aiFilledType && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start space-x-2">
                  <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-blue-800">
                    AI has automatically detected and filled the Tag and Type based on your document.
                    You can change these if needed.
                  </p>
                </div>
              )}

              {/* Tag and Type Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label htmlFor="file-tags-select" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center justify-between">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-1.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Tag
                    </div>
                    <span className="text-xs text-blue-600 font-semibold bg-blue-50 px-2 py-0.5 rounded-full flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      AI
                    </span>
                  </label>
                  <select
                    id="file-tags-select"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-gray-400 transition-all duration-200 cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-800 font-medium"
                    value={selectedTag}
                    onChange={(e) => handleTagChange(e.target.value)}
                    disabled={loadingMeta || addingKnowledgeFile}
                  >
                    <option value="">Select a tag...</option>
                    {tags.map((tag, index) => (
                      <option key={index} value={tag}>
                        {tag}
                      </option>
                    ))}
                  </select>
                </div>

                {types.length > 0 && (
                  <div>
                    <label htmlFor="file-types-select-auto" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center justify-between">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1.5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Type
                      </div>
                      {aiFilledType && (
                        <span className="text-xs text-purple-600 font-semibold bg-purple-50 px-2 py-0.5 rounded-full flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          AI
                        </span>
                      )}
                    </label>
                    <select
                      id="file-types-select-auto"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white hover:border-gray-400 transition-all duration-200 cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-800 font-medium"
                      value={selectedType}
                      onChange={(e) => {
                        setSelectedType(e.target.value);
                        setAiFilledType(false);
                      }}
                      disabled={loadingMeta || addingKnowledgeFile}
                    >
                      <option value="">Select a type...</option>
                      {types.map((type, index) => (
                        <option key={index} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Validation Warning */}
              {validation && validation.warning && (
                <div
                  className={`mb-6 p-4 rounded-xl border-2 shadow-lg transition-all duration-300 ${
                    validation.is_reasonable
                      ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-400'
                      : 'bg-gradient-to-br from-red-50 to-pink-50 border-red-400'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      validation.is_reasonable ? 'bg-yellow-400' : 'bg-red-400'
                    }`}>
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium leading-relaxed ${
                        validation.is_reasonable ? 'text-yellow-900' : 'text-red-900'
                      }`}>
                        {validation.warning}
                      </p>
                      {validation.suggested_alternative && (
                        <button
                          className="mt-3 px-4 py-2 bg-white border-2 border-blue-400 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-50 hover:border-blue-500 transition-all duration-200 shadow-sm hover:shadow-md"
                          onClick={() => handleTagChange(validation.suggested_alternative!)}
                        >
                          Use "{validation.suggested_alternative}" instead
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Confirm Button */}
              <button
                className="relative w-full px-8 py-4 bg-adalabs-blue-500 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none overflow-hidden group"
                onClick={handleConfirmTag}
                disabled={addingKnowledgeFile || !selectedTag ||
                  (types.length > 0 && !selectedType)}
              >
                <div className="absolute inset-0 bg-adalabs-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                <div className="relative flex items-center justify-center space-x-2">
                  {addingKnowledgeFile ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Confirming...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Confirm Tag and Process File</span>
                    </>
                  )}
                </div>
              </button>
            </div>
          )}

          {uploadedFilename && !awaitingConfirmation && !requestingSuggestion && (
            <>
              {/* Select dropdowns for uploaded file - only show if lists have items */}
              {(tags.length > 0 || types.length > 0 || parsingStrategies.length > 0) && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {tags.length > 0 && (
                    <div>
                      <label htmlFor="file-tags-select" className="block text-sm font-medium text-green-700 mb-2">
                        Select Tags
                      </label>
                      <select
                        id="file-tags-select"
                        className="w-full px-3 py-2 border border-green-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                        value={selectedTag}
                        onChange={(e) => setSelectedTag(e.target.value)}
                        disabled={loadingMeta}
                      >
                        <option value="">Select a tag...</option>
                        {tags.map((tag, index) => (
                          <option key={index} value={tag}>
                            {tag}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {types.length > 0 && (
                    <div>
                      <label htmlFor="file-types-select" className="block text-sm font-medium text-green-700 mb-2">
                        Select Types
                      </label>
                      <select
                        id="file-types-select"
                        className="w-full px-3 py-2 border border-green-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        disabled={loadingMeta}
                      >
                        <option value="">Select a type...</option>
                        {types.map((type, index) => (
                          <option key={index} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {parsingStrategies.length > 0 && (
                    <div>
                      <label htmlFor="parsing-strategy-select" className="block text-sm font-medium text-green-700 mb-2">
                        Select Parsing Strategy
                      </label>
                      <select
                        id="parsing-strategy-select"
                        className="w-full px-3 py-2 border border-green-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                        value={selectedParsingStrategy}
                        onChange={(e) => setSelectedParsingStrategy(e.target.value)}
                        disabled={loadingMeta}
                      >
                        <option value="">Select a strategy...</option>
                        {parsingStrategies.map((strategy, index) => (
                          <option key={index} value={strategy}>
                            {strategy}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {loadingMeta && (
                <div className="mt-2 text-sm text-green-600">
                  Loading options...
                </div>
              )}

              <button
                className="btn-primary mt-3 w-full"
                onClick={handleAddKnowledgeFile}
                disabled={addingKnowledgeFile || isPolling ||
                  (tags.length > 0 && !selectedTag) ||
                  (types.length > 0 && !selectedType) ||
                  (parsingStrategies.length > 0 && !selectedParsingStrategy)}
              >
                {addingKnowledgeFile ? 'Adding...' : isPolling ? 'Processing...' : 'Add'}
              </button>
            </>
          )}
        </div>
      )}
      
      {isPolling && (
        <div className="mt-4 p-4 rounded-lg bg-yellow-100 border border-yellow-300 text-yellow-800">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-2"></div>
            Processing file... Please wait
          </div>
        </div>
      )}
      
      {addNotification && (
        <div className={`mt-4 p-4 rounded-lg ${
          addNotification.type === 'success' 
            ? 'bg-green-100 border border-green-300 text-green-800' 
            : 'bg-red-100 border border-red-300 text-red-800'
        }`}>
          {addNotification.message}
        </div>
      )}
    </div>
  );
};

export default KnowledgeFileUpload;
